/* jshint ignore:start */
import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function TrendCard({ title, unit, series }) {
  const last = series[series.length - 1];
  const first = series[0];
  const delta = last && first && last.value != null && first.value != null
    ? last.value - first.value
    : null;
  return (
    <div className="border rounded-[16px] bg-white p-4 min-h-[132px]">
      <p className="text-[12px] uppercase tracking-wide font-[700] text-rm-primary">{title}</p>
      {last ? (
        <>
          <p className="text-[28px] font-[800] text-rm-ink leading-none mt-2">
            {last.value}
            <span className="text-[13px] font-[500] text-rm-muted ml-1">{unit}</span>
          </p>
          <p className="text-[13px] text-rm-muted mt-2">
            {series.length} reading{series.length === 1 ? "" : "s"}
            {delta != null && series.length > 1 ? ` · ${delta > 0 ? "+" : ""}${delta.toFixed(1)} vs first` : ""}
          </p>
          <p className="text-[12px] text-rm-muted mt-1">Latest {formatDate(last.date)}</p>
        </>
      ) : (
        <p className="text-rm-muted text-[14px] mt-4">No readings yet</p>
      )}
    </div>
  );
}

export default function HealthRecords() {
  const { ApiBaseUrl, setIsLoginShow } = useContext(MyContext);
  const Userid = secureLocalStorage.getItem("Userid");
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [data, setData] = useState(null);

  const load = (fid) => {
    if (!Userid) {
      setIsLoginShow(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .post(`${ApiBaseUrl}/customer/health_records`, {
        id: Userid,
        family_mem_id: fid || undefined,
      })
      .then((r) => {
        if (r.data.Result) {
          setData(r.data);
          setMembers(r.data.members || []);
        } else {
          showToast({ title: r.data.message || "Could not load health records", id: "error" });
          setData(null);
        }
      })
      .catch(() => showToast({ title: "Health records are unavailable right now", id: "error" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(memberId);
    /* eslint-disable-next-line */
  }, [Userid, ApiBaseUrl, memberId]);

  const alerts = data?.alerts || [];
  const readings = useMemo(() => (data?.readings || []).slice(0, 40), [data]);
  const reports = (data?.lab_reports || []).filter((r) => r.file_path);

  return (
    <div className="width-container py-[40px]">
      <p className="text-[13px] uppercase tracking-wide font-[700] text-rm-primary">Your chart</p>
      <h1 className="text-[28px] font-[800] text-rm-ink mt-1">Health records</h1>
      <p className="text-rm-muted mt-2 max-w-2xl">
        Vitals from clinic visits and home-care reports, with HbA1c and out-of-range alerts.
        Lab files stay attached as reports — numeric lab series appear once a result is entered as a vital.
      </p>

      {members.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-6">
          <button
            type="button"
            onClick={() => setMemberId("")}
            className={`px-3 py-1.5 rounded-full text-[13px] font-[600] ${
              memberId === "" ? "bg-rm-primary text-white" : "bg-white border text-rm-ink"
            }`}
          >
            Everyone
          </button>
          {members.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMemberId(String(m.id))}
              className={`px-3 py-1.5 rounded-full text-[13px] font-[600] ${
                memberId === String(m.id) ? "bg-rm-primary text-white" : "bg-white border text-rm-ink"
              }`}
            >
              {m.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-center text-rm-muted py-16">Loading records…</p>
      ) : !data ? (
        <div className="text-center py-16 border rounded-[16px] bg-white mt-8">
          <p className="font-[600] text-lg">No records yet</p>
          <p className="text-rm-muted mt-1">Book a consult or home-care visit to start a chart.</p>
        </div>
      ) : (
        <>
          {alerts.length > 0 && (
            <div className="mt-8 border rounded-[16px] bg-white p-4">
              <h2 className="font-[800] text-rm-ink">Out of range</h2>
              <ul className="mt-3 space-y-2">
                {alerts.slice(0, 8).map((a, i) => (
                  <li key={`${a.type}-${a.date}-${i}`} className="text-[14px]">
                    <span className={`font-[700] ${a.severity === "high" ? "text-red-600" : "text-amber-700"}`}>
                      {a.title}
                    </span>
                    <span className="text-rm-muted"> · {a.message}</span>
                    <span className="text-rm-muted"> · {formatDate(a.date)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            <TrendCard title="HbA1c" unit="%" series={data.trends?.hba1c || []} />
            <TrendCard title="Glucose" unit="mg/dL" series={data.trends?.glucose || []} />
            <TrendCard title="Systolic BP" unit="mmHg" series={data.trends?.bp_systolic || []} />
            <TrendCard title="Weight" unit="kg" series={data.trends?.weight || []} />
          </div>

          <div className="mt-8 border rounded-[16px] bg-white overflow-hidden">
            <div className="px-4 py-3 border-b">
              <h2 className="font-[800] text-rm-ink">Readings</h2>
            </div>
            {readings.length === 0 ? (
              <p className="p-6 text-rm-muted">No vitals recorded for this member yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[14px]">
                  <thead className="text-[12px] uppercase tracking-wide text-rm-muted">
                    <tr>
                      <th className="px-4 py-2 font-[600]">Date</th>
                      <th className="px-4 py-2 font-[600]">Reading</th>
                      <th className="px-4 py-2 font-[600]">Value</th>
                      <th className="px-4 py-2 font-[600]">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readings.map((r, i) => (
                      <tr key={`${r.source}-${r.source_id}-${r.kind}-${i}`} className="border-t">
                        <td className="px-4 py-2 whitespace-nowrap">{formatDate(r.date)}</td>
                        <td className="px-4 py-2">{r.title}</td>
                        <td className={`px-4 py-2 font-[700] ${r.severity === "high" ? "text-red-600" : r.severity === "warn" ? "text-amber-700" : "text-rm-ink"}`}>
                          {r.value != null ? `${r.value}${r.unit ? ` ${r.unit}` : ""}` : r.value_text || "—"}
                        </td>
                        <td className="px-4 py-2 text-rm-muted capitalize">{String(r.source || "").replace(/_/g, " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {reports.length > 0 && (
            <div className="mt-8 border rounded-[16px] bg-white p-4">
              <h2 className="font-[800] text-rm-ink">Lab reports</h2>
              <ul className="mt-3 space-y-2">
                {reports.map((r) => (
                  <li key={`${r.booking_id}-${r.file_path}`}>
                    <a
                      className="text-rm-primary font-[600]"
                      href={`${ApiBaseUrl}/${String(r.file_path).replace(/^\//, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Booking #{r.booking_id}
                    </a>
                    <span className="text-rm-muted text-[13px]"> · {formatDate(r.uploaded_at || r.booking_date)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
/* jshint ignore:end */
